// ============================================================================
// NOTIFICATION HUB SERVICE
// ============================================================================
// Talks to prima-api /notifications/inbox*, which proxies the notifications-hub
// inbox for the current authenticated user. The hub API key never reaches the
// browser; auth, tenant and app headers are added by the axios interceptors.

import { get, post } from '../client/axiosClient';
import { notificationEndpoints } from '../endpoints/notification.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  InAppInboxPage,
  UnreadCountResponse,
  MarkReadRequest,
} from '../types';

class NotificationHubService {
  async getInbox(page = 1, pageSize = 20, unreadOnly = false): Promise<InAppInboxPage> {
    const endpoint = buildEndpointWithQuery(notificationEndpoints.basePath(), {
      page,
      pageSize,
      ...(unreadOnly ? { unreadOnly: true } : {}),
    });
    const response = await get<ApiResponse<InAppInboxPage>>(endpoint);
    const data = response.data?.data;
    return {
      items: data?.items ?? [],
      page: data?.page ?? page,
      pageSize: data?.pageSize ?? pageSize,
      totalCount: data?.totalCount ?? 0,
      totalPages: data?.totalPages ?? 0,
    };
  }

  async getUnreadCount(): Promise<number> {
    const response = await get<ApiResponse<UnreadCountResponse>>(notificationEndpoints.unreadCount());
    return response.data?.data?.unreadCount ?? 0;
  }

  async markRead(request: MarkReadRequest): Promise<void> {
    await post(notificationEndpoints.markRead(), request);
  }

  async markAllRead(): Promise<void> {
    await post(notificationEndpoints.markRead(), { markAllRead: true });
  }

  async markUnread(channelIds: string[]): Promise<void> {
    await post(notificationEndpoints.markRead(), { channelIds, asUnread: true });
  }

  async archive(channelId: string): Promise<void> {
    await post(notificationEndpoints.archive(channelId), null);
  }
}

export const notificationHubService = new NotificationHubService();
export default notificationHubService;
